import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Mail, Megaphone, Trash2 } from "lucide-react";
import PatientAppLayout from "../../components/PatientAppLayout/PatientAppLayout";
import { showConfirm } from "../../feedback/feedbackApi";
import "./PatientProfileSubpages.scss";

const STORAGE = {
  push: "healbid:patient:pref:notifyPush",
  email: "healbid:patient:pref:notifyEmail",
  marketing: "healbid:patient:pref:notifyMarketing",
};

function readBool(key, defaultTrue) {
  try {
    const s = localStorage.getItem(key);
    if (s == null) return defaultTrue;
    return s === "1" || s === "true";
  } catch {
    return defaultTrue;
  }
}

function writeBool(key, v) {
  try {
    localStorage.setItem(key, v ? "1" : "0");
  } catch {
    /* ignore */
  }
}

const DELETE_PREFILL =
  "I want to permanently delete my HealBid account and all associated data (profile, documents, bids, and messages). Please confirm the next steps and anything you need from me to verify this request.";

export default function PatientProfileSettingsPage() {
  const navigate = useNavigate();
  const [pushOn, setPushOn] = useState(() => readBool(STORAGE.push, true));
  const [emailOn, setEmailOn] = useState(() => readBool(STORAGE.email, true));
  const [marketingOn, setMarketingOn] = useState(() => readBool(STORAGE.marketing, false));

  useEffect(() => {
    writeBool(STORAGE.push, pushOn);
  }, [pushOn]);
  useEffect(() => {
    writeBool(STORAGE.email, emailOn);
  }, [emailOn]);
  useEffect(() => {
    writeBool(STORAGE.marketing, marketingOn);
  }, [marketingOn]);

  const handleDeleteAccount = useCallback(async () => {
    const ok1 = await showConfirm(
      "Deleting your account removes access to bids, chats, documents, and care history tied to this login. Our care team verifies your identity before data is permanently erased.",
      {
        title: "Delete account?",
        confirmText: "Continue",
        cancelText: "Cancel",
        danger: true,
      }
    );
    if (!ok1) return;

    const ok2 = await showConfirm(
      "Open the help assistant with a ready-to-send message? You can edit it before sending.",
      {
        title: "Contact care team",
        confirmText: "Open chat",
        cancelText: "Not now",
        danger: true,
      }
    );
    if (!ok2) return;

    navigate("/patient-support", {
      state: { prefillDraft: DELETE_PREFILL },
    });
  }, [navigate]);

  return (
    <PatientAppLayout activeNav="profile">
      <div className="patient-profile-sub patient-profile-sub--settings">
        <div className="patient-profile-sub__card">
          <h1 className="patient-profile-sub__h1">Settings</h1>
          <p className="patient-profile-sub__lede">
            Notification preferences are stored on this device. Critical safety and account messages may still be sent.
          </p>

          <section className="patient-profile-sub__block" aria-labelledby="notif-heading">
            <h2 id="notif-heading" className="patient-profile-sub__h2">
              Notifications
            </h2>
            <ul className="patient-profile-sub__toggles">
              <li>
                <label className="patient-profile-sub__toggle">
                  <span className="patient-profile-sub__toggleIcon" aria-hidden>
                    <Bell size={18} strokeWidth={1.85} />
                  </span>
                  <span className="patient-profile-sub__toggleBody">
                    <span className="patient-profile-sub__toggleTitle">In-app &amp; push alerts</span>
                    <span className="patient-profile-sub__toggleHint">Bids, messages, and appointment updates</span>
                  </span>
                  <input
                    type="checkbox"
                    className="patient-profile-sub__toggleInput"
                    checked={pushOn}
                    onChange={(e) => setPushOn(e.target.checked)}
                  />
                </label>
              </li>
              <li>
                <label className="patient-profile-sub__toggle">
                  <span className="patient-profile-sub__toggleIcon" aria-hidden>
                    <Mail size={18} strokeWidth={1.85} />
                  </span>
                  <span className="patient-profile-sub__toggleBody">
                    <span className="patient-profile-sub__toggleTitle">Email updates</span>
                    <span className="patient-profile-sub__toggleHint">Summaries and non-urgent notices</span>
                  </span>
                  <input
                    type="checkbox"
                    className="patient-profile-sub__toggleInput"
                    checked={emailOn}
                    onChange={(e) => setEmailOn(e.target.checked)}
                  />
                </label>
              </li>
              <li>
                <label className="patient-profile-sub__toggle">
                  <span className="patient-profile-sub__toggleIcon" aria-hidden>
                    <Megaphone size={18} strokeWidth={1.85} />
                  </span>
                  <span className="patient-profile-sub__toggleBody">
                    <span className="patient-profile-sub__toggleTitle">Product &amp; tips</span>
                    <span className="patient-profile-sub__toggleHint">Optional messages about HealBid features</span>
                  </span>
                  <input
                    type="checkbox"
                    className="patient-profile-sub__toggleInput"
                    checked={marketingOn}
                    onChange={(e) => setMarketingOn(e.target.checked)}
                  />
                </label>
              </li>
            </ul>
          </section>

          <section className="patient-profile-sub__block patient-profile-sub__block--danger" aria-labelledby="danger-heading">
            <h2 id="danger-heading" className="patient-profile-sub__h2">
              Account
            </h2>
            <p className="patient-profile-sub__prose">
              To remove your profile, documents, and activity from HealBid, request deletion below. Our team confirms
              identity before data is erased.
            </p>
            <button type="button" className="patient-profile-sub__deleteBtn" onClick={handleDeleteAccount}>
              <Trash2 size={18} strokeWidth={1.85} aria-hidden />
              <span>Delete account &amp; everything</span>
            </button>
          </section>
        </div>
      </div>
    </PatientAppLayout>
  );
}
